import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { NotificationService } from 'core/services/notification';
import { HttpService } from 'core/services/http';
import {
    IconButton,
    Badge,
    Popover,
    Box,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Divider,
    Button,
    Stack,
    Chip,
    CircularProgress
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import WorkIcon from '@mui/icons-material/Work';
import MessageIcon from '@mui/icons-material/Message';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

function NotificationDropdown() {
    const [anchorEl, setAnchorEl] = useState(null);
    const [displayCount, setDisplayCount] = useState(5);
    const listRef = useRef(null);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const open = Boolean(anchorEl);
    const user = useMemo(() => HttpService.getUser?.() ?? null, []);
    const isEmployee = user?.role === 'employee';
    const isEmployer = user?.role === 'employer';

    const { data: notificationsData, isLoading } = useQuery(
        'notifications',
        async () => {
            return await NotificationService.getNotifications();
        },
        {
            refetchInterval: 30000, // Refetch every 30 seconds
            enabled: open, // Only fetch when dropdown is open
        }
    );

    const markAsReadMutation = useMutation(
        async (notificationId) => {
            return await NotificationService.markAsRead(notificationId);
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('notifications');
                queryClient.invalidateQueries('unreadNotificationsCount');
            }
        }
    );

    const notifications = notificationsData?.results || notificationsData || [];
    const displayedNotifications = notifications.slice(0, displayCount);
    const unreadCount = notifications.filter(n => !n.is_read).length;
    const hasMore = notifications.length > displayCount;

    // Reset display count when dropdown closes
    useEffect(() => {
        if (!open) {
            setDisplayCount(5);
        }
    }, [open]);

    // Handle scroll to load more
    useEffect(() => {
        const listElement = listRef.current;
        if (!listElement || !hasMore || !open) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = listElement;
            // Load more when user scrolls near the bottom (within 50px)
            if (scrollHeight - scrollTop - clientHeight < 50) {
                setDisplayCount(prev => {
                    const newCount = Math.min(prev + 5, notifications.length);
                    return newCount;
                });
            }
        };

        listElement.addEventListener('scroll', handleScroll);
        return () => listElement.removeEventListener('scroll', handleScroll);
    }, [hasMore, notifications.length, displayCount, open]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleNotificationClick = async (notification) => {
        if (!notification.is_read) {
            await markAsReadMutation.mutateAsync(notification.id);
        }
        handleClose();

        // Navigate based on notification type
        if (notification.type === 'application_status' && notification.type_id) {
            navigate(`/jobseeker/applications/${notification.type_id}`);
        } else if (notification.type === 'apply' && notification.type_id) {
            navigate(`/employer/applications/${notification.type_id}`);
        } else if (notification.type === 'message' && notification.type_id) {
            // Navigate to messages if implemented
        }
    };

    const handleViewAll = () => {
        handleClose();
        // Navigate to the appropriate dashboard notifications route
        if (isEmployee) {
            navigate('/jobseeker/notifications');
        } else if (isEmployer) {
            navigate('/employer/notifications');
        } else {
            navigate('/notifications');
        }
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'application_status':
            case 'apply':
                return <WorkIcon fontSize="small" />;
            case 'message':
                return <MessageIcon fontSize="small" />;
            default:
                return <InfoIcon fontSize="small" />;
        }
    };

    return (
        <>
            <IconButton
                color="inherit"
                onClick={handleClick}
                aria-label="notifications"
            >
                <Badge badgeContent={unreadCount} color="error">
                    {unreadCount > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
                </Badge>
            </IconButton>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: { width: 360, maxHeight: 500, mt: 1 }
                }}
            >
                <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" fontWeight={600}>
                            Notifications
                        </Typography>
                        {unreadCount > 0 && (
                            <Chip
                                label={`${unreadCount} new`}
                                color="primary"
                                size="small"
                            />
                        )}
                    </Box>
                    <Divider sx={{ mb: 1 }} />
                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : displayedNotifications.length === 0 ? (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                No notifications
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <List 
                                ref={listRef}
                                sx={{ maxHeight: 400, overflow: 'auto', p: 0 }}
                            >
                                {displayedNotifications.map((notification) => (
                                    <ListItem
                                        key={notification.id}
                                        disablePadding
                                        sx={{
                                            bgcolor: notification.is_read ? 'transparent' : 'action.hover',
                                            '&:hover': {
                                                bgcolor: 'action.selected'
                                            }
                                        }}
                                    >
                                        <ListItemButton
                                            onClick={() => handleNotificationClick(notification)}
                                            sx={{ py: 1.5 }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, width: '100%' }}>
                                                <Box sx={{ mt: 0.5 }}>
                                                    {getNotificationIcon(notification.type)}
                                                </Box>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={notification.is_read ? 400 : 600}
                                                        sx={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            mb: 0.5
                                                        }}
                                                    >
                                                        {notification.title}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis'
                                                        }}
                                                    >
                                                        {notification.description}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                        {new Date(notification.created_at).toLocaleDateString('en-US', {
                                                            month: 'short',
                                                            day: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </Typography>
                                                </Box>
                                                {!notification.is_read && (
                                                    <Box
                                                        sx={{
                                                            width: 8,
                                                            height: 8,
                                                            borderRadius: '50%',
                                                            bgcolor: 'primary.main',
                                                            mt: 1
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                            <Divider sx={{ my: 1 }} />
                            <Button
                                fullWidth
                                variant="text"
                                onClick={handleViewAll}
                                sx={{ textTransform: 'none' }}
                            >
                                View All Notifications
                            </Button>
                        </>
                    )}
                </Box>
            </Popover>
        </>
    );
}

export default NotificationDropdown;

